import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { GitCompare, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppState";
import { PROPERTIES, formatPrezzo } from "@/data/properties";

const ROWS = [
  { label: "Prezzo", get: (p) => formatPrezzo(p) },
  { label: "Comune / Zona", get: (p) => `${p.comune} — ${p.zona}` },
  { label: "Tipologia", get: (p) => p.tipo },
  { label: "Superficie", get: (p) => `${p.superficie} mq` },
  { label: "Locali", get: (p) => p.locali },
  { label: "Camere", get: (p) => p.camere },
  { label: "Bagni", get: (p) => p.bagni },
  { label: "Classe energetica", get: (p) => p.classeEnergetica },
  { label: "Spese condominiali", get: (p) => (p.speseCondominiali ? `€ ${p.speseCondominiali}/mese` : "—") },
  { label: "Box / Posto auto", get: (p) => (p.box || p.postoAuto ? "Sì" : "No") },
  { label: "Ascensore", get: (p) => (p.ascensore ? "Sì" : "No") },
];

export default function CompareBar() {
  const { compare, toggleCompare, clearCompare } = useApp();
  const [open, setOpen] = useState(false);
  const items = PROPERTIES.filter((p) => compare.includes(p.id));

  return (
    <>
      <AnimatePresence>
        {items.length > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-4 inset-x-4 z-40 mx-auto max-w-3xl"
            data-testid="compare-bar"
          >
            <div className="rounded-2xl bg-anthracite text-white shadow-2xl px-4 py-3 flex items-center gap-3">
              <div className="flex -space-x-3">
                {items.map((p) => (
                  <img key={p.id} src={p.immagini[0]} alt="" className="h-10 w-10 rounded-full object-cover border-2 border-anthracite" />
                ))}
              </div>
              <span className="text-sm font-medium flex-1">{items.length} immobili selezionati</span>
              <button onClick={clearCompare} className="text-sm text-white/60 hover:text-white transition-colors" data-testid="compare-clear">Svuota</button>
              <button
                onClick={() => setOpen(true)}
                disabled={items.length < 2}
                className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold transition-all hover:bg-brand-hover disabled:opacity-40"
                data-testid="compare-open"
              >
                <GitCompare className="h-4 w-4" /> Confronta
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl bg-warm border-0 rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="compare-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-semibold text-anthracite">Confronto immobili</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left p-2"></th>
                  {items.map((p) => (
                    <th key={p.id} className="p-2 align-top min-w-[150px]">
                      <img src={p.immagini[0]} alt="" className="h-24 w-full object-cover rounded-xl" />
                      <div className="mt-2 flex items-start justify-between gap-1">
                        <span className="font-semibold text-anthracite text-left leading-tight">{p.titolo}</span>
                        <button onClick={() => toggleCompare(p.id)} className="text-anthracite/40 hover:text-brand"><X className="h-4 w-4" /></button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r, i) => (
                  <tr key={r.label} className={i % 2 ? "bg-white/60" : ""}>
                    <td className="p-2 font-semibold text-anthracite/60">{r.label}</td>
                    {items.map((p) => (
                      <td key={p.id} className="p-2 text-anthracite">{r.get(p)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
